/**
 * Common locale validation utilities for CMS content
 * Used by both client and server code for consistent locale handling
 */

import { UI_TO_CMS_MAP, SUPPORTED_LOCALES, DEFAULT_LOCALE, SupportedLocale } from './cmsLocaleConstants';

/**
 * Returns the language code portion from a locale string.
 * Examples: "en-au" => "en", "pt_BR" => "pt", "ja" => "ja"
 */
export const getLanguageCodeFromLocale = (locale: string): string => {
    const [languageCode] = locale.split(/[-_]/);
    return languageCode?.toLowerCase() || '';
};

/**
 * Validates and normalizes a locale parameter
 * @param {string | null | undefined} locale - Locale code to validate
 * @returns {SupportedLocale} Valid locale code or default locale
 */
export const validateLocale = (locale: string | null | undefined): SupportedLocale => {
    if (!locale || typeof locale !== 'string') {
        return DEFAULT_LOCALE;
    }

    const normalized = locale.toLowerCase();

    if (!SUPPORTED_LOCALES.includes(normalized as SupportedLocale)) {
        return DEFAULT_LOCALE;
    }

    return normalized as SupportedLocale;
};

/**
 * Maps UI locale codes to CMS locales and returns null for invalid values.
 * @param {string | null | undefined} userLocale - User's preferred locale
 * @returns {SupportedLocale | null} Locale to use for CMS requests
 */
export const getOptionalLocaleForCMS = (userLocale?: string | null): SupportedLocale | null => {
    if (!userLocale || typeof userLocale !== 'string') {
        return null;
    }

    const normalized = userLocale.toLowerCase();

    if (SUPPORTED_LOCALES.includes(normalized as SupportedLocale)) {
        return normalized as SupportedLocale;
    }

    const cmsLocale = UI_TO_CMS_MAP[normalized as keyof typeof UI_TO_CMS_MAP];
    if (cmsLocale) {
        return cmsLocale;
    }

    const normalizedLanguageCode = getLanguageCodeFromLocale(normalized);
    const mappedLocale = UI_TO_CMS_MAP[normalizedLanguageCode as keyof typeof UI_TO_CMS_MAP];

    return mappedLocale || null;
};

/**
 * Maps UI locale codes to CMS locales
 * @param {string | null | undefined} userLocale - User's preferred locale
 * @returns {SupportedLocale} Locale to use for CMS requests
 */
export const getLocaleForCMS = (userLocale?: string | null): SupportedLocale => {
    return getOptionalLocaleForCMS(userLocale) || DEFAULT_LOCALE;
};
